import React, { useEffect, useMemo, useState } from 'react';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useGetUserNotificationsPageQuery } from '../../entities/notifications/api/notificationsApi';
import { useSeenNotifications } from '../../entities/notifications/model/seen';
import { formatYekb, isActive, useNowYekb } from '../../entities/notifications/model/window';
import type { NotificationStatus } from '../../entities/notifications/model/types';
import { formatRangeLabel } from '../../utils/format';
import { cx } from '../../utils/cx';
import styles from './UserNotificationsPage.module.scss';
import { DataTable } from '../../components/ui/DataTable/DataTable';
import { SearchField } from '../../components/ui/SearchField/SearchField';
import { useAppSelector } from '../../app/hooks';
import { selectUser } from '../../entities/session/model/slice';

const STATUS_LABEL: Record<NotificationStatus, string> = {
    planned: 'Запланировано',
    active: 'Активно',
    done: 'Завершено',
    unknown: 'Без срока',
};

const EMPTY = 'Не указано';

export function UserNotificationsPage() {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');

    const query = useDebouncedValue(search, 400);

    const user = useAppSelector(selectUser);
    const userId = user?.email;

    const { seen, markSeen } = useSeenNotifications(userId);
    const now = useNowYekb();

    const { data, isLoading, isFetching, isError, refetch } = useGetUserNotificationsPageQuery({
        pageNum: page,
        pageSize,
    });

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, data?.pages ?? 1);

    useEffect(() => {
        const shown = items.filter((item) => isActive(item, now)).map((item) => item.id);
        if (shown.length) markSeen(shown);
    }, [items, now, markSeen]);

    const unread = useMemo(
        () => items.filter((item) => isActive(item, now) && !seen.has(item.id)).length,
        [items, now, seen],
    );

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return items;
        return items.filter(
            (item) =>
                item.text.toLowerCase().includes(needle) ||
                item.author.toLowerCase().includes(needle),
        );
    }, [items, query]);

    const changePage = (next: number) => setPage(Math.min(Math.max(1, next), pageCount));

    const changePageSize = (next: number) => {
        setPageSize(next);
        setPage(1);
    };

    return (
        <>
            <PageHeader
                title="Уведомления"
                meta={unread > 0 ? [`Непрочитанных: ${unread}`] : undefined}
            />

            <section className={styles.panel}>
                <div className={styles.tools}>
                    <SearchField
                        value={search}
                        onChange={setSearch}
                        placeholder="Поиск по тексту или автору"
                    />
                </div>

                {isError ? (
                    <div className={styles.state}>
                        <p>Не удалось загрузить уведомления.</p>
                        <Button onClick={() => refetch()}>Повторить</Button>
                    </div>
                ) : (
                    <DataTable
                        static
                        loading={isFetching}
                        isLoading={isLoading}
                        loadingText="Загружаем уведомления…"
                        scrollKey={`${page}-${pageSize}-${query}`}
                        footer={
                            total > 0 && !query ? (
                                <TableFooter
                                    page={page}
                                    totalPages={pageCount}
                                    rangeLabel={formatRangeLabel(page, pageSize, total)}
                                    onPageChange={changePage}
                                />
                            ) : undefined
                        }
                        empty={
                            isLoading ? (
                                <EmptyState title="Загружаем…" description="Секунду, забираем список." />
                            ) : visible.length === 0 ? (
                                <EmptyState
                                    title={query ? 'Ничего не нашлось' : 'Уведомлений пока нет'}
                                    description={
                                        query
                                            ? 'Поиск идёт по загруженной странице — попробуйте изменить запрос или перейти на другую страницу.'
                                            : 'Здесь будут появляться сообщения о работах на платформе.'
                                    }
                                />
                            ) : undefined
                        }
                    >
                        <thead>
                            <tr>
                                <th>Создано</th>
                                <th>Текст</th>
                                <th>Начало</th>
                                <th>Завершение</th>
                                <th>Статус</th>
                                <th>Автор</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visible.map((item) => (
                                <tr
                                    key={item.id}
                                    className={cx(
                                        isActive(item, now) && !seen.has(item.id) && styles.unread,
                                    )}
                                >
                                    <td>{formatYekb(item.createdAtStamp) || EMPTY}</td>
                                    <td>
                                        <span className={styles.text} title={item.text}>
                                            {item.text}
                                        </span>
                                    </td>
                                    <td>{formatYekb(item.startsAt) || EMPTY}</td>
                                    <td>{formatYekb(item.endsAt) || EMPTY}</td>
                                    <td>
                                        <span className={styles.pill} data-status={item.status}>
                                            {STATUS_LABEL[item.status]}
                                        </span>
                                    </td>
                                    <td>{item.author}</td>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                )}
            </section>
        </>
    );
}
